<template>
  <div class="brf pb-2">
    <div class="mb-2" v-if="!loading">
      <p v-if="!isFormAnswered" class="mb-2">Template: {{ templateName }}</p>

      <FormulateForm
        ref="form"
        debounce
        :schema="schema"
        v-model="model"
        @submit="onSubmit"
        @created="onFormReady"
        @failed-validation="onValidate"
      />
    </div>

    <div class="text-center" v-if="loading">
      <b-spinner label="Spinning" variant="primary"></b-spinner>
    </div>
  </div>
</template>

<script>
import axios from "@axios";
import moment from "moment";
import { getUserData } from "@/auth/utils";
import RiskFormNormalizer from "./RiskFormNormalizer.js";
import FileUploadWithPreview from "./FileUploadWithPreview.vue";
import ReviewCommentsWithPreview from "./ReviewCommentsWithPreview.vue";
import SectionGroup from "./SectionGroup.vue";
import DurationHmsInput from "./DurationHmsInput.vue";
import UserPickerInput from "./UserPickerInput.vue";
import UnitPickerInput from "./UnitPickerInput.vue";
import RiskFormAnswers from "@/components/BuildRiskForm/RiskFormAnswers.vue";

export default {
  name: "BuildRiskForm",
  components: {
    RiskFormAnswers,
    FileUploadWithPreview,
    ReviewCommentsWithPreview,
    SectionGroup,
    DurationHmsInput,
  },
  props: {
    templateId: { type: [Number, String], default: null },
    templateSnapshot: { type: Array, default: () => [] },
    templateSourceIds: { type: Array, default: () => [] },
    riskEvaluationId: { type: [Number, String], required: true },
    data: { type: [Array, Object], default: () => ({}) },
    currentState: { type: String, default: "in_progress" },
    isReviewer: { type: Boolean, default: false },
    canEdit: { type: Boolean, default: true },
  },
  data() {
    return {
      loading: true,
      templateName: "",
      templateData: [],
      localTemplateId: null,
      localRiskEvaluationId: null,
      schema: [],
      model: {},
      formData: null,
      filesData: {},
      riskFormId: null,
      idTenant: null,
      isFormAnswered: false,
      evaluation: null,
      statuses: [],
      lastStatus: null,
      incomingSnapshot: [],
      incomingSources: [],
      _normalizer: null,
      _syncingSchema: false,
      _pendingSnapshot: null,
      uploadingFieldLabel: null,
      deletingFieldLabel: null,

      // Autosave
      autoSaveEnabled: true,
      asDebounceMs: 3000,
      asMaxIntervalMs: 20000,
      _autoDirty: false,
      _asSaving: false,
      _asLastHash: "",
      _asDebounce: null,
      _asMaxTimer: null,

      // NO/NA sync: only call _syncFlaggedAnswers when the NO/NA list actually changed
      _asNoNaHash: "",
      _loadingLock: false,
    };
  },
  async mounted() {
    this.localRiskEvaluationId = this.riskEvaluationId || null;
    this.incomingSnapshot = Array.isArray(this.templateSnapshot)
      ? this.templateSnapshot
      : [];
    this.incomingSources = Array.isArray(this.templateSourceIds)
      ? this.templateSourceIds
      : [];
    await this.reloadForm();
  },
  beforeDestroy() {
    this._clearAutoTimers();
  },
  watch: {
    model: {
      deep: true,
      handler() {
        if (this._syncingSchema || this.loading) return;
        this.rebuildDynamicSchema();
        this._emitFlaggedPreview();
        this._onModelTouched();
      },
    },
    currentState(nextState) {
      if (!this._normalizer) return;
      this._normalizer.currentState = nextState || "in_progress";
      this.rebuildDynamicSchema();
      this._emitFlaggedPreview();
      if (this._isLocked()) this._clearAutoTimers();
    },
    isReviewer(nextIsReviewer) {
      if (!this._normalizer) return;
      this._normalizer.isReviewer = !!nextIsReviewer;
      this.rebuildDynamicSchema();
      this._emitFlaggedPreview();
      if (this._isLocked()) this._clearAutoTimers();
    },
    canEdit(nextCanEdit) {
      if (!this._normalizer) return;
      this._normalizer.options = this._normalizer.options || {};
      this._normalizer.options.canEdit = nextCanEdit;
      this._normalizer.options.disabled = nextCanEdit === false;
      this.rebuildDynamicSchema();
      this._emitFlaggedPreview();
      if (this._isLocked()) this._clearAutoTimers();
    },
    uploadingFieldLabel() {
      this.rebuildDynamicSchema();
      this._emitFlaggedPreview();
    },
    deletingFieldLabel() {
      this.rebuildDynamicSchema();
      this._emitFlaggedPreview();
    },
  },
  computed: {
    showAnswersOnlyWhenApproved() {
      return this.lastStatus === "approved";
    },
    showAnswersOnFinalStates() {
      return ["approved", "rejected", "scheduled", "canceled"].includes(this.lastStatus);
    },
  },
  methods: {
    // Saves using the autosave path (no UI loading state, no NO/NA sync by default).
    async saveDraft({ silent = true } = {}) {
      await this.onSubmit(undefined, { autosave: true, silent });
    },

    // Forces pending autosave to finish before continuing.
    async flushPending({ timeoutMs = 120000 } = {}) {
      const startedAt = Date.now();

      if (this._autoDirty) {
        await this._autoSaveNow("flush");
      }

      while (this._asSaving) {
        if (Date.now() - startedAt > timeoutMs) break;
        await new Promise((resolve) => setTimeout(resolve, 80));
      }
    },

    // Exposes busy state to parent containers.
    isBusy() {
      return (
        this.loading ||
        this._asSaving ||
        this._autoDirty ||
        this.uploadingFieldLabel != null ||
        this.deletingFieldLabel != null
      );
    },

    // Builds the current NO/NA list directly from the form model (front-end source of truth).
    getFlaggedPreview() {
      const preview = [];

      if (!this._normalizer) {
        return { items: preview, riskFormAnswerId: this.riskFormId || null };
      }

      const baseline = this._normalizer.pruneModelInvisible(this.model || {});
      const processedFields = this._normalizer.getProcessedFields() || [];

      for (const fieldDefinition of processedFields) {
        if (!fieldDefinition || !fieldDefinition.item_id) continue;

        const fieldType = String(fieldDefinition.type || "");
        if (!["select", "radio", "checkbox"].includes(fieldType)) continue;

        const answerValue = this._normalizer.getAnswer(baseline, fieldDefinition);
        const normalizedAnswer = this._normalizeNoNaAnswer(answerValue);

        if (!normalizedAnswer) continue;

        const questionText = String(
          fieldDefinition.label || fieldDefinition.question || fieldDefinition.name || ""
        ).trim();

        preview.push({
          template_item_id: String(fieldDefinition.item_id),
          question: questionText || String(fieldDefinition.name || ""),
          section: fieldDefinition.section || null,
          subsection: fieldDefinition.subsection || null,
          answer: normalizedAnswer,
        });
      }

      return { items: preview, riskFormAnswerId: this.riskFormId || null };
    },

    // Emits the NO/NA preview so RiskFlaggedAnswersPart can render immediately.
    _emitFlaggedPreview() {
      try {
        const payload = this.getFlaggedPreview();
        this.$emit("flagged-preview", payload);
      } catch (_) {}
    },

    // Syncs NO/NA rows to backend (used for critical actions like send to review).
    async syncFlaggedAnswersNow() {
      if (!this.ensureEvaluationId()) return;
      const baseline = this._normalizer
        ? this._normalizer.pruneModelInvisible(this.model || {})
        : this.model || {};
      await this._syncFlaggedAnswers(baseline);
    },

    onFormReady() {},
    onValidate() {},

    slugify(text) {
      return String(text || "")
        .toLowerCase()
        .trim()
        .replace(/[^\w\s-]/g, "")
        .replace(/\s+/g, "-")
        .replace(/-+/g, "-");
    },

    buildToc() {
      if (!this._normalizer) return [];
      const processedFields = this._normalizer.getProcessedFields();
      const { order, structured } =
        this._normalizer.groupBySectionSubsection(processedFields);
      return order.map((sectionName) => {
        const entry = structured.get(sectionName) || { subOrder: [] };
        return {
          id: `sec-${this.slugify(sectionName)}`,
          label: sectionName,
          children: (entry.subOrder || []).map((subsectionName) => ({
            id: `sub-${this.slugify(sectionName)}-${this.slugify(subsectionName)}`,
            label: subsectionName,
          })),
        };
      });
    },

    emitToc() {
      this.$emit("toc", this.buildToc());
    },

    submitForm() {
      this._syncingSchema = true;
      try {
        this._pendingSnapshot = JSON.parse(JSON.stringify(this.model || {}));
      } catch (_) {
        this._pendingSnapshot = { ...(this.model || {}) };
      }

      const formRef = this.$refs?.form || null;
      if (formRef && typeof formRef.submit === "function") return formRef.submit();

      const formElement = this.$refs?.form?.$el || null;
      if (formElement) {
        const submitEvent = new Event("submit", { cancelable: true });
        formElement.dispatchEvent(submitEvent);
        return;
      }

      this.onSubmit();
    },

    async checkIfFormExists() {
      return axios.get(
        `/v1/risk_form_answers/get-form-by-evaluation/${this.localRiskEvaluationId}`
      );
    },

    async reloadForm() {
      this.loading = true;
      this.localTemplateId = this.templateId;

      try {
        if (!this.localRiskEvaluationId && this.riskEvaluationId) {
          this.localRiskEvaluationId = this.riskEvaluationId;
        }

        if (!this.localRiskEvaluationId) {
          await this.fetchTemplateOnlyOrSnapshot();
        } else {
          const response = await this.checkIfFormExists();
          const formPayload = response.data.data;

          this.riskFormId = formPayload.id || null;
          const rawData = formPayload.data;
          this.model =
            rawData && typeof rawData === "object" && !Array.isArray(rawData)
              ? rawData
              : {};
          this.formData = formPayload;
          this.localTemplateId = formPayload.id_template || this.localTemplateId;
          this.templateName = formPayload.template?.name || "Composed template";
          this.templateData =
            Array.isArray(formPayload.template_snapshot) &&
            formPayload.template_snapshot.length
              ? formPayload.template_snapshot
              : formPayload.template && Array.isArray(formPayload.template.data)
              ? formPayload.template.data
              : [];
          this.idTenant = formPayload.template?.id_tenant || null;

          this.evaluation = formPayload.evaluation || null;
          this.statuses = Array.isArray(this.evaluation?.statuses)
            ? this.evaluation.statuses
            : [];
          this.lastStatus = this.evaluation?.last_status?.name;

          this.isFormAnswered = true;
        }
      } catch (_) {
        this.riskFormId = null;
        this.isFormAnswered = false;
        await this.fetchTemplateOnlyOrSnapshot();
      }

      RiskFormNormalizer.hydrateLegacyAnswers(this.templateData, this.model);

      this.initializeFormNormalizer();
      this.rebuildDynamicSchema();
      this._syncFilesDataFromModel();

      const baseline = this._normalizer
        ? this._normalizer.pruneModelInvisible(this.model || {})
        : this.model || {};
      this._asLastHash = this._hash(baseline);
      this._autoDirty = false;
      this._clearAutoTimers();

      // Reset NO/NA hash so the first save always syncs
      this._asNoNaHash = "";

      this.loading = false;

      this._emitFlaggedPreview();
    },

    async fetchTemplateOnlyOrSnapshot() {
      if (this.incomingSnapshot && this.incomingSnapshot.length) {
        this.templateName = "Composed template";
        this.templateData = this.incomingSnapshot;
        this.idTenant = null;
        this.localTemplateId = null;
        this.model = {};
        return;
      }

      if (!this.localTemplateId) {
        this.templateName = "Composed template";
        this.templateData = [];
        this.model = {};
        return;
      }

      const { data } = await axios.get(`/v1/risk_templates/${this.localTemplateId}`);
      this.templateName = data.name;
      this.templateData = data.data;
      this.idTenant = data.id_tenant;
      this.model = {};
    },

    initializeFormNormalizer() {
      const effectiveState = this.lastStatus || this.currentState || "in_progress";

      this._normalizer = new RiskFormNormalizer(this, this.templateData, {
        disabled: this.canEdit === false,
        canEdit: this.canEdit,
        currentState: effectiveState,
        isReviewer: this.isReviewer === true,
        customComponents: {
          FileUploadWithPreview,
          ReviewCommentsWithPreview,
          SectionGroup,
          DurationHmsInput,
          UserPickerInput,
          UnitPickerInput,
        },
        model: this.model,
        onUpdateFiles: this.handleFileUpload,
        onUpdateComments: ({ label, comments }) => {
          this.$set(this.model, label, comments || []);
          this._emitFlaggedPreview();
          this._onModelTouched();
        },
        onAddComment: this.addCommentAndSave,
        uploadingFieldLabel: this.uploadingFieldLabel,
        deletingFieldLabel: this.deletingFieldLabel,
      });

      this.schema = this._normalizer.convert();
      this.$nextTick(() => this.emitToc());
    },

    rebuildDynamicSchema() {
      if (!this._normalizer) return;
      this._syncingSchema = true;

      if (this._normalizer.options) {
        this._normalizer.options.uploadingFieldLabel = this.uploadingFieldLabel;
        this._normalizer.options.deletingFieldLabel = this.deletingFieldLabel;
      }

      try {
        this._normalizer.model = this.model || {};
        this.schema = this._normalizer.convert();
      } finally {
        this.$nextTick(() => {
          this._syncingSchema = false;
          this.emitToc();
        });
      }
    },

    handleFileUpload({ files, label }) {
      const previousLength = (this.filesData[label] || []).length;
      if (!this.filesData[label]) this.$set(this.filesData, label, []);

      this.filesData[label] = (files || []).map((fileItem) => ({
        url: fileItem.url || "",
        name: fileItem.name,
        raw: fileItem.raw || null,
      }));

      const hasRawFiles = (files || []).some((fileItem) => fileItem && fileItem.raw);
      if (hasRawFiles) {
        this.uploadingFieldLabel = label;
        this.uploadFilesForField(label, this.filesData[label]);
        return;
      }

      const newLength = (files || []).length;
      if (newLength < previousLength) {
        this.deletingFieldLabel = label;
        this.uploadFilesForField(label, this.filesData[label]);
        return;
      }

      this._onModelTouched();
    },

    async addCommentAndSave({ label, text, files }) {
      if (!this.ensureEvaluationId()) {
        throw new Error(
          this.$t?.("risk.missing_evaluation") || "Missing risk evaluation id"
        );
      }

      const userEmail = getUserData()?.email || "anonymous@domain";
      const newComment = {
        text: text || "",
        date: new Date().toISOString(),
        user: userEmail,
        files: (files || []).map((file) => ({
          name: file.name,
          url: "",
          raw: file,
        })),
      };

      const existingComments = Array.isArray(this.model[label]) ? this.model[label] : [];
      const updatedComments = [...existingComments, newComment];
      this.$set(this.model, label, updatedComments);

      const baseline = this._normalizer
        ? this._normalizer.pruneModelInvisible(this.model || {})
        : { ...this.model };
      const finalData = { ...baseline };

      const visibleFileLabels = this._normalizer
        ? this._normalizer.visibleFileLabels(baseline)
        : new Set();
      for (const fieldName in this.filesData) {
        if (fieldName.startsWith("review_comments_")) continue;
        if (visibleFileLabels.size && !visibleFileLabels.has(fieldName)) continue;

        const allFiles = this.filesData[fieldName] || [];
        finalData[fieldName] = allFiles
          .filter((fileItem) => fileItem.url && String(fileItem.url).trim() !== "")
          .map((fileItem) => ({ url: fileItem.url || "", name: fileItem.name }));
      }

      const reviewCommentKeys = Object.keys(this.model || {}).filter((key) =>
        key.startsWith("review_comments_")
      );
      reviewCommentKeys.forEach((reviewKey) => {
        const commentsArray = Array.isArray(this.model[reviewKey])
          ? this.model[reviewKey]
          : [];
        finalData[reviewKey] = commentsArray.map((commentItem) => ({
          text: commentItem.text,
          date: commentItem.date,
          user: commentItem.user,
          files: Array.isArray(commentItem.files)
            ? commentItem.files.map((fileItem) => ({
                url:
                  fileItem.url && !String(fileItem.url).startsWith("blob:")
                    ? fileItem.url
                    : "",
                name: fileItem.name,
              }))
            : [],
        }));
      });

      const requestFormData = this._buildFormData(finalData);
      const url = this.riskFormId
        ? `/v1/risk_form_answers/${this.riskFormId}`
        : `/v1/risk_form_answers`;
      const config = {
        headers: {
          "Content-Type": "multipart/form-data",
          "X-HTTP-Method-Override": this.riskFormId ? "PUT" : "POST",
        },
      };

      const response = await axios.post(url, requestFormData, config);
      const payload = response.data?.data ?? response.data;

      if (payload && payload.id) this.riskFormId = payload.id;

      this._asLastHash = this._hash(baseline);
      this._autoDirty = false;
      this._clearAutoTimers();
      this._clearTemporaryFiles();
      this.rebuildDynamicSchema();

      this._emitFlaggedPreview();
    },

    async uploadFilesForField(label, files) {
      if (!this.ensureEvaluationId()) {
        this.uploadingFieldLabel = null;
        this.deletingFieldLabel = null;
        this.$toast?.error?.(
          this.$t?.("risk.missing_evaluation") || "Missing risk evaluation id"
        );
        return;
      }

      const requestFormData = this._buildFormDataForField(label, files);
      const url = this.riskFormId
        ? `/v1/risk_form_answers/${this.riskFormId}`
        : `/v1/risk_form_answers`;
      const config = {
        headers: {
          "Content-Type": "multipart/form-data",
          "X-HTTP-Method-Override": this.riskFormId ? "PUT" : "POST",
        },
      };

      try {
        const response = await axios.post(url, requestFormData, config);
        const payload = response.data?.data ?? response.data;

        if (payload && payload.id) this.riskFormId = payload.id;

        const savedData =
          payload?.data ?? response.data?.data?.data ?? response.data?.data;
        if (savedData && Array.isArray(savedData[label])) {
          const fileList = savedData[label].map((fileItem) => ({
            url: fileItem.url || "",
            name: fileItem.name || "",
            raw: null,
          }));
          this.$set(this.filesData, label, fileList);
          this.$set(this.model, label, fileList);
        }
      } catch (error) {
        this.$toast?.error?.(this.$t?.("risk.save_failed") || "No se pudo guardar");
        this.$emit("submitError", { error });
      } finally {
        this.uploadingFieldLabel = null;
        this.deletingFieldLabel = null;
        this._emitFlaggedPreview();
      }
    },

    _buildFormDataForField(fieldName, allFiles) {
      const requestFormData = new FormData();

      const baseline = this._normalizer
        ? this._normalizer.pruneModelInvisible(this.model || {})
        : { ...this.model };
      const fileListForData = (allFiles || []).map((fileItem) => ({
        url: fileItem.raw ? "" : fileItem.url || "",
        name: fileItem.name || "",
      }));

      const finalData = { ...baseline, [fieldName]: fileListForData };

      for (const fileItem of allFiles || []) {
        if (fileItem.raw) requestFormData.append(`${fieldName}[]`, fileItem.raw);
      }

      requestFormData.append(
        "template_snapshot",
        JSON.stringify(this.templateData || [])
      );
      requestFormData.append(
        "template_sources",
        JSON.stringify(this.incomingSources || [])
      );
      if (this.localTemplateId)
        requestFormData.append("id_template", this.localTemplateId);
      if (this.idTenant) requestFormData.append("id_tenant", this.idTenant);
      requestFormData.append("id_risk_evaluation", this.localRiskEvaluationId);
      requestFormData.append("data", JSON.stringify(finalData));

      return requestFormData;
    },

    _hasNewFiles() {
      try {
        for (const fieldName in this.filesData) {
          if (fieldName.startsWith("review_comments_")) continue;
          const files = this.filesData[fieldName] || [];
          if (files.some((fileItem) => fileItem.raw)) return true;
        }

        const reviewCommentKeys = Object.keys(this.model || {}).filter((key) =>
          key.startsWith("review_comments_")
        );
        for (const reviewKey of reviewCommentKeys) {
          const comments = Array.isArray(this.model[reviewKey])
            ? this.model[reviewKey]
            : [];
          for (const commentItem of comments) {
            const commentFiles = Array.isArray(commentItem.files)
              ? commentItem.files
              : [];
            if (
              commentFiles.some(
                (fileItem) => fileItem.raw || !fileItem.url || fileItem.url.trim() === ""
              )
            ) {
              return true;
            }
          }
        }

        return false;
      } catch {
        return false;
      }
    },

    _clearTemporaryFiles() {
      for (const fieldName in this.filesData) {
        const files = this.filesData[fieldName] || [];
        const cleanedFiles = files.filter((fileItem) => {
          if (fileItem.raw) return false;
          if (fileItem.url && fileItem.url.startsWith("blob:")) return false;
          return true;
        });
        if (cleanedFiles.length !== files.length) {
          this.$set(this.filesData, fieldName, cleanedFiles);
        }
      }
    },

    _syncFilesDataFromModel() {
      if (!this.model || !this.templateData || !this._normalizer) return;

      const fileFields = [];
      const findFileFields = (nodes) => {
        (nodes || []).forEach((node) => {
          if (node.type === "files_section" && node.name) fileFields.push(node);
          if (Array.isArray(node.children)) findFileFields(node.children);
        });
      };
      findFileFields(this.templateData);

      fileFields.forEach((fieldDefinition) => {
        const fieldKey = this._normalizer.answerKey(fieldDefinition);
        const modelFilesRaw = this._normalizer.getAnswer(this.model, fieldDefinition);
        const modelFiles = Array.isArray(modelFilesRaw) ? modelFilesRaw : [];
        const backendFiles = modelFiles.filter(
          (fileItem) => fileItem.url && !fileItem.url.startsWith("blob:")
        );
        this.$set(
          this.filesData,
          fieldKey,
          backendFiles.map((fileItem) => ({
            url: fileItem.url || "",
            name: fileItem.name || "",
            raw: null,
          }))
        );
      });
    },

    ensureEvaluationId() {
      if (!this.localRiskEvaluationId) {
        this.localRiskEvaluationId =
          this.riskEvaluationId ||
          this.formData?.id_risk_evaluation ||
          this.evaluation?.id ||
          null;
      }
      return !!this.localRiskEvaluationId;
    },

    _ctxModel() {
      try {
        return this.$refs?.form?.context?.model || null;
      } catch (_) {
        return null;
      }
    },

    _hasKeys(objectValue) {
      return (
        objectValue &&
        typeof objectValue === "object" &&
        !Array.isArray(objectValue) &&
        Object.keys(objectValue).length > 0
      );
    },

    _clone(objectValue) {
      try {
        return JSON.parse(JSON.stringify(objectValue || {}));
      } catch (_) {
        return { ...(objectValue || {}) };
      }
    },

    _normalizeNoNaString(value) {
      const normalized = String(value || "")
        .trim()
        .toLowerCase();
      if (!normalized) return null;
      if (normalized === "no" || normalized === "no." || normalized === "no ")
        return "NO";
      if (normalized === "na" || normalized === "n/a") return "NA";
      if (normalized.includes("no aplica")) return "NA";
      return null;
    },

    _normalizeNoNaAnswer(answerValue) {
      if (Array.isArray(answerValue)) {
        let code = null;
        for (const optionValue of answerValue) {
          const matched = this._normalizeNoNaString(optionValue);
          if (matched === "NO") return "NO";
          if (matched === "NA") code = code || "NA";
        }
        return code;
      }
      return this._normalizeNoNaString(answerValue);
    },

    async _syncFlaggedAnswers(baseline = {}, { deleteOrphans = true } = {}) {
      const evaluationId = this.localRiskEvaluationId;
      if (!evaluationId) return;

      const desiredRows = this.getFlaggedPreview().items;

      let existingRows = [];
      try {
        const response = await axios.get("/v1/flagged_answers", {
          params: { id_risk_evaluation: evaluationId, per_page: 500 },
        });
        const rowsPayload =
          (response.data && (response.data.data || response.data)) || [];
        existingRows = Array.isArray(rowsPayload.data) ? rowsPayload.data : rowsPayload;
      } catch (_) {
        existingRows = [];
      }

      const existingByTemplateItemId = new Map();
      existingRows.forEach((row) =>
        existingByTemplateItemId.set(String(row.template_item_id), row)
      );

      const desiredByTemplateItemId = new Map();
      desiredRows.forEach((row) =>
        desiredByTemplateItemId.set(String(row.template_item_id), row)
      );

      for (const desiredRow of desiredRows) {
        const payload = {
          id_risk_evaluation: Number(evaluationId),
          id_risk_form_answer: this.riskFormId || null,
          template_item_id: desiredRow.template_item_id,
          question: desiredRow.question,
          section: desiredRow.section,
          subsection: desiredRow.subsection,
          answer: desiredRow.answer,
        };
        try {
          await axios.post("/v1/flagged_answers", payload);
        } catch (error) {
          console.error("flagged_answers upsert error", error);
        }
      }

      if (deleteOrphans) {
        for (const [templateItemId, existingRow] of existingByTemplateItemId.entries()) {
          if (!desiredByTemplateItemId.has(templateItemId)) {
            try {
              await axios.delete(`/v1/flagged_answers/${existingRow.id}`);
            } catch (error) {
              console.error("flagged_answers delete error", error);
            }
          }
        }
      }
    },

    _buildFormData(finalData) {
      const requestFormData = new FormData();

      const visibleFileLabels = this._normalizer
        ? this._normalizer.visibleFileLabels(finalData)
        : new Set();

      for (const fieldName in this.filesData) {
        if (fieldName.startsWith("review_comments_")) continue;
        if (visibleFileLabels.size && !visibleFileLabels.has(fieldName)) continue;

        const allFiles = this.filesData[fieldName] || [];
        for (const fileItem of allFiles) {
          if (fileItem.raw) requestFormData.append(`${fieldName}[]`, fileItem.raw);
        }
      }

      const reviewKeys = Object.keys(finalData).filter((key) =>
        key.startsWith("review_comments_")
      );
      reviewKeys.forEach((reviewKey) => {
        const commentsArr = Array.isArray(this.model[reviewKey])
          ? this.model[reviewKey]
          : [];
        commentsArr.forEach((commentItem, commentIndex) => {
          const files = Array.isArray(commentItem.files) ? commentItem.files : [];
          files.forEach((fileObject) => {
            let fileToSend = null;

            if (fileObject.raw instanceof File || fileObject.raw instanceof Blob) {
              fileToSend = fileObject.raw;
            } else if (fileObject instanceof File || fileObject instanceof Blob) {
              fileToSend = fileObject;
            } else if (
              fileObject.file &&
              (fileObject.file instanceof File || fileObject.file instanceof Blob)
            ) {
              fileToSend = fileObject.file;
            } else if (
              fileObject.originFileObj &&
              (fileObject.originFileObj instanceof File ||
                fileObject.originFileObj instanceof Blob)
            ) {
              fileToSend = fileObject.originFileObj;
            }

            const needsAppend =
              !fileObject.url ||
              fileObject.url.trim() === "" ||
              String(fileObject.url || "").startsWith("blob:");

            if (fileToSend && needsAppend) {
              requestFormData.append(
                `${reviewKey}[${commentIndex}][files][]`,
                fileToSend
              );
            }
          });
        });
      });

      requestFormData.append(
        "template_snapshot",
        JSON.stringify(this.templateData || [])
      );
      requestFormData.append(
        "template_sources",
        JSON.stringify(this.incomingSources || [])
      );
      if (this.localTemplateId)
        requestFormData.append("id_template", this.localTemplateId);
      if (this.idTenant) requestFormData.append("id_tenant", this.idTenant);
      requestFormData.append("id_risk_evaluation", this.localRiskEvaluationId);
      requestFormData.append("data", JSON.stringify(finalData));

      return requestFormData;
    },

    async onSubmit(values, opts = {}) {
      const isAutosave = opts.autosave === true;

      if (!isAutosave) this.loading = true;

      if (!this.ensureEvaluationId()) {
        if (!isAutosave) {
          this.loading = false;
          this.$toast?.error?.(
            this.$t?.("risk.missing_evaluation") || "Missing risk evaluation id"
          );
        }
        this._pendingSnapshot = null;
        this._syncingSchema = false;
        return;
      }

      const isEvent = values && typeof values.preventDefault === "function";

      let fresh;
      if (this._hasKeys(this._pendingSnapshot)) {
        fresh = this._pendingSnapshot;
      } else if (!isEvent && this._hasKeys(values)) {
        fresh = values;
      } else {
        const contextModel = this._ctxModel();
        fresh = this._hasKeys(contextModel)
          ? this._clone(contextModel)
          : this._clone(this.model);
      }

      const baseline = this._normalizer
        ? this._normalizer.pruneModelInvisible(fresh)
        : fresh;

      const useJsonUpdate = !this._hasNewFiles() && !!this.riskFormId;
      const finalData = { ...baseline };

      const visibleFileLabels = this._normalizer
        ? this._normalizer.visibleFileLabels(baseline)
        : new Set();
      for (const fieldName in this.filesData) {
        if (fieldName.startsWith("review_comments_")) continue;
        if (visibleFileLabels.size && !visibleFileLabels.has(fieldName)) continue;

        const allFiles = this.filesData[fieldName] || [];
        finalData[fieldName] = allFiles
          .filter((fileItem) => fileItem.url && String(fileItem.url).trim() !== "")
          .map((fileItem) => ({ url: fileItem.url || "", name: fileItem.name }));
      }

      const reviewKeys = Object.keys(finalData).filter((key) =>
        key.startsWith("review_comments_")
      );
      reviewKeys.forEach((reviewKey) => {
        const commentsArr = Array.isArray(this.model[reviewKey])
          ? this.model[reviewKey]
          : [];
        finalData[reviewKey] = commentsArr.map((commentItem) => ({
          text: commentItem.text,
          date: commentItem.date,
          user: commentItem.user,
          files: Array.isArray(commentItem.files)
            ? commentItem.files.map((fileItem) => ({
                url:
                  fileItem.url && !String(fileItem.url).startsWith("blob:")
                    ? fileItem.url
                    : "",
                name: fileItem.name,
              }))
            : [],
        }));
      });

      const url = this.riskFormId
        ? `/v1/risk_form_answers/${this.riskFormId}`
        : `/v1/risk_form_answers`;
      const multipartConfig = {
        headers: {
          "Content-Type": "multipart/form-data",
          "X-HTTP-Method-Override": this.riskFormId ? "PUT" : "POST",
        },
      };

      const jsonPayload = {
        data: finalData,
        id_risk_evaluation: this.localRiskEvaluationId,
        template_snapshot: this.templateData || [],
        template_sources: this.incomingSources || [],
      };
      if (this.localTemplateId) jsonPayload.id_template = this.localTemplateId;
      if (this.idTenant) jsonPayload.id_tenant = this.idTenant;

      try {
        let saveResponse = null;

        if (useJsonUpdate) {
          try {
            saveResponse = await axios.post(
              `/v1/risk_form_answers/${this.riskFormId}`,
              jsonPayload,
              {
                headers: {
                  "Content-Type": "application/json",
                  "X-HTTP-Method-Override": "PUT",
                },
              }
            );
          } catch (_) {
            saveResponse = await axios.post(
              url,
              this._buildFormData(finalData),
              multipartConfig
            );
          }
        } else {
          saveResponse = await axios.post(
            url,
            this._buildFormData(finalData),
            multipartConfig
          );
        }

        const savedPayload = saveResponse?.data?.data ?? saveResponse?.data ?? null;
        if (savedPayload && savedPayload.id) this.riskFormId = savedPayload.id;

        this._asLastHash = this._hash(baseline);
        this._autoDirty = false;
        this._clearAutoTimers();

        // ─── Sync NO/NA flagged_answers after every successful save
        // Only calls backend if the NO/NA list actually changed since last sync
        try {
          const noNaPreview = this.getFlaggedPreview();
          const noNaHash = this._hash(noNaPreview.items);
          if (noNaHash !== this._asNoNaHash) {
            this._asNoNaHash = noNaHash;
            await this._syncFlaggedAnswers(baseline, { deleteOrphans: false });
            await new Promise((r) => setTimeout(r, 600));
            this.$emit("flagged-synced");
          }
        } catch (_) {}

        const hasNewFiles = this._hasNewFiles();

        if (!isAutosave) {
          this.$emit("submit");
        }

        if (hasNewFiles) this._clearTemporaryFiles();
        if (isAutosave && hasNewFiles) await this.reloadForm();
      } catch (error) {
        this.$emit("submitError", { error });
        if (isAutosave) this.$emit("auto-save-error", error);
      } finally {
        if (!isAutosave) this.loading = false;
        this._pendingSnapshot = null;
        this._syncingSchema = false;
        this._emitFlaggedPreview();
      }
    },

    // Autosave utilities
    _isLocked() {
      if (this.canEdit === false) return true;

      const state = String(
        this._normalizer?.currentState || this.currentState || ""
      ).toLowerCase();
      if (state === "in_progress" || state === "reassigned") return false;
      if (state === "in_review") return !this.isReviewer;
      return true;
    },

    _hash(value) {
      try {
        return JSON.stringify(value);
      } catch (_) {
        return String(Date.now());
      }
    },

    _onModelTouched() {
      if (this.uploadingFieldLabel != null || this.deletingFieldLabel != null) return;
      if (!this.autoSaveEnabled || this.loading || this._isLocked()) return;

      this._autoDirty = true;

      clearTimeout(this._asDebounce);
      this._asDebounce = setTimeout(
        () => this._autoSaveNow("debounce"),
        this.asDebounceMs
      );

      if (!this._asMaxTimer) {
        this._asMaxTimer = setTimeout(
          () => this._autoSaveNow("max"),
          this.asMaxIntervalMs
        );
      }
    },

    _clearAutoTimers() {
      clearTimeout(this._asDebounce);
      this._asDebounce = null;
      clearTimeout(this._asMaxTimer);
      this._asMaxTimer = null;
    },

    async _autoSaveNow(reason = "debounce") {
      if (this.uploadingFieldLabel != null || this.deletingFieldLabel != null) return;
      if (!this._autoDirty || this._asSaving || this.loading || this._isLocked()) return;

      try {
        this._pendingSnapshot = JSON.parse(JSON.stringify(this.model || {}));
      } catch (_) {
        this._pendingSnapshot = { ...(this.model || {}) };
      }

      const baseline = this._normalizer
        ? this._normalizer.pruneModelInvisible(this._pendingSnapshot)
        : this._pendingSnapshot;

      const hash = this._hash(baseline);
      if (hash === this._asLastHash) {
        this._autoDirty = false;
        this._clearAutoTimers();
        return;
      }

      this._asSaving = true;
      this.$emit("auto-save-start", { reason });

      try {
        await this.onSubmit(undefined, { autosave: true });
        this.$emit("auto-save-done", { reason });
      } finally {
        this._asSaving = false;
        this._clearAutoTimers();

        if (this._autoDirty) {
          this._asDebounce = setTimeout(
            () => this._autoSaveNow("debounce"),
            this.asDebounceMs
          );
        }
      }
    },

    _cleanEmptyValues(model) {
      if (!model || typeof model !== "object") return {};
      const cleaned = {};
      for (const key in model) {
        if (!Object.prototype.hasOwnProperty.call(model, key)) continue;
        const value = model[key];
        if (value === null || value === undefined) continue;
        if (typeof value === "string" && value.trim() === "") continue;
        if (Array.isArray(value) && value.length === 0) continue;
        cleaned[key] = value;
      }
      return cleaned;
    },

    async validateBeforeReview() {
      if (!this._normalizer) return { ok: true, missing: [] };

      const currentModel = this._cleanEmptyValues(this.model || {});
      const answers = this._normalizer.pruneModelInvisible(currentModel);
      const missing = [];

      const processedFields = this._normalizer.getProcessedFields() || [];
      const { order, groups } = this._normalizer.groupBySection(processedFields);

      order.forEach((sectionName) => {
        const sectionGroup = groups.get(sectionName) || { fields: [] };
        const sectionFields = sectionGroup.fields || [];
        const indexMap = new Map(
          sectionFields.map((fieldDefinition, index) => [fieldDefinition.item_id, index])
        );

        sectionFields.forEach((fieldDefinition) => {
          if (
            fieldDefinition.type === "section_header" ||
            fieldDefinition.type === "subsection_header"
          )
            return;

          const index = indexMap.has(fieldDefinition.item_id)
            ? indexMap.get(fieldDefinition.item_id)
            : 0;
          const isVisible = this._normalizer.isFieldVisible(
            fieldDefinition,
            sectionFields,
            index,
            answers
          );
          if (!isVisible) return;

          if (!this._isAnswerFilled(fieldDefinition, answers)) {
            missing.push({
              name: fieldDefinition.name,
              section: fieldDefinition.section || sectionName || "",
              subsection: fieldDefinition.subsection || null,
            });
          }
        });
      });

      if (missing.length) return { ok: false, missing };
      return { ok: true, missing: [] };
    },

    async validateBeforeSave() {
      return this.validateBeforeReview();
    },

    _isAnswerFilled(fieldDefinition, answers) {
      const type = String(fieldDefinition.type || "");
      const value = this._normalizer
        ? this._normalizer.getAnswer(answers, fieldDefinition)
        : answers[fieldDefinition.name];

      const hasValue = (inputValue) =>
        !(inputValue === undefined || inputValue === null || String(inputValue) === "");

      if (type === "duration_hm") {
        const hours = this._normalizer
          ? this._normalizer.getDerived(answers, fieldDefinition, "horas")
          : answers[`${fieldDefinition.name} - horas`];
        const minutes = this._normalizer
          ? this._normalizer.getDerived(answers, fieldDefinition, "minutos")
          : answers[`${fieldDefinition.name} - minutos`];
        return hasValue(hours) && hasValue(minutes);
      }

      if (type === "duration_ms") {
        const minutes = this._normalizer
          ? this._normalizer.getDerived(answers, fieldDefinition, "minutos")
          : answers[`${fieldDefinition.name} - minutos`];
        const seconds = this._normalizer
          ? this._normalizer.getDerived(answers, fieldDefinition, "segundos")
          : answers[`${fieldDefinition.name} - segundos`];
        return hasValue(minutes) && hasValue(seconds);
      }

      if (type === "duration_hms") {
        const timeValue = value;
        if (!timeValue || typeof timeValue !== "object") return false;
        const hours = Number(timeValue.hours) || 0;
        const minutes = Number(timeValue.minutes) || 0;
        const seconds = Number(timeValue.seconds) || 0;
        return hours > 0 || minutes > 0 || seconds > 0;
      }

      if (type === "checkbox") {
        return Array.isArray(value) && value.length > 0;
      }

      if (type === "files_section") {
        return true;
      }

      return hasValue(value);
    },

    formatDate(date) {
      return moment(date).format("YYYY-MM-DD HH:mm:ss");
    },
  },
};
</script>

<style scoped>
.brf {
  width: 100%;
}
.brf .formulate-form {
  width: 100%;
}
.brf .formulate-input {
  margin-bottom: 12px;
}
.brf .formulate-input .formulate-input-label {
  font-weight: 600;
  margin-bottom: 4px;
}
.brf .formulate-input .formulate-input-element {
  width: 100%;
}
.brf .field-divider {
  margin: 16px 0;
  border: 0;
  border-top: 1px solid #edf0f3;
}
.brf .duration-hm-row {
  display: grid;
  grid-template-columns: minmax(140px, 1fr) minmax(140px, 1fr);
  gap: 12px;
  align-items: end;
}
</style>
